import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import { LuPlus, LuPencil, LuTrash2, LuEye } from "react-icons/lu";
import { useDataTable } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    PageHeader,
    ThemedText,
} from "@/components/ui";
import { formatCurrency, formatDate } from "@/utils";
import { useUser } from "@/context/UserContext";

const SalesPaymentData = () => {
    const { hasPermission } = useUser();
    const initialFilters = { search: "" };

    const table = useDataTable(
        "sales-payment/data",
        "sales-payment/{id}/delete",
        {},
        initialFilters,
    );

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-12" },
        { name: "No. Pembayaran", skeletonWidth: "w-32" },
        { name: "Tanggal", skeletonWidth: "w-24" },
        { name: "Customer", skeletonWidth: "w-40" },
        { name: "Jml. Invoice", skeletonWidth: "w-20", align: "end" },
        { name: "Total Diskon", skeletonWidth: "w-28", align: "end" },
        { name: "Total Pembayaran", skeletonWidth: "w-36", align: "end" },
        { name: "Aksi", skeletonWidth: "w-16", align: "end" },
    ];

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data pelunasan penjualan."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Pembayaran Penjualan"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Penjualan", href: "/sales-order" },
                        { label: "Pembayaran Penjualan" },
                    ]}
                />
                {hasPermission("sales-payment.create") && (
                    <Button
                        as={Link}
                        to="/sales-payment/create"
                        color="primary"
                        startContent={<LuPlus />}
                        size="sm"
                    >
                        Buat Pembayaran
                    </Button>
                )}
            </div>

            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Data"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada data yang cocok dengan filter."
                                    : "Belum ada data pembayaran penjualan."
                            }
                        />
                    ) : (
                        <Table
                            aria-label="Tabel Pembayaran Penjualan"
                            shadow="none"
                        >
                            <TableHeader>
                                <TableColumn>No.</TableColumn>
                                <TableColumn>No. Pembayaran</TableColumn>
                                <TableColumn>Tanggal</TableColumn>
                                <TableColumn>Customer</TableColumn>
                                <TableColumn align="end">
                                    Jml. Invoice
                                </TableColumn>
                                <TableColumn align="end">
                                    Total Diskon
                                </TableColumn>
                                <TableColumn align="end">
                                    Total Pembayaran
                                </TableColumn>
                                <TableColumn align="end">Aksi</TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {(table.pagination.page - 1) *
                                                    table.pagination.perPage +
                                                    table.items.indexOf(item) +
                                                    1}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                className="font-medium"
                                            >
                                                {item.payment_number}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {formatDate(
                                                    item.payment_date,
                                                    "dd MMM yyyy",
                                                )}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.customer?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex justify-end">
                                                <ThemedText size="sm">
                                                    {item.item_count}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex justify-end">
                                                <ThemedText
                                                    size="sm"
                                                    className={
                                                        item.total_discount > 0
                                                            ? "text-danger"
                                                            : ""
                                                    }
                                                >
                                                    {item.total_discount > 0
                                                        ? formatCurrency(
                                                              item.total_discount,
                                                          )
                                                        : "-"}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col items-end">
                                                <ThemedText
                                                    size="sm"
                                                    className="font-medium"
                                                >
                                                    {formatCurrency(
                                                        item.total_amount,
                                                    )}
                                                </ThemedText>
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                >
                                                    {item.master_cash?.name ||
                                                        "-"}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex justify-end gap-1">
                                                <Tooltip content="Lihat Detail">
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="light"
                                                        as={Link}
                                                        to={`/sales-payment/${item.id}/detail`}
                                                    >
                                                        <LuEye
                                                            size={14}
                                                            className="text-default-500"
                                                        />
                                                    </Button>
                                                </Tooltip>
                                                {hasPermission("sales-payment.edit") && (
                                                    <Tooltip content="Edit">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            as={Link}
                                                            to={`/sales-payment/${item.id}/edit`}
                                                        >
                                                            <LuPencil
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {hasPermission("sales-payment.delete") && (
                                                    <Tooltip
                                                        content="Hapus"
                                                        color="danger"
                                                    >
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            color="danger"
                                                            onPress={() =>
                                                                table.deleteState.openDeleteModal(
                                                                    item,
                                                                )
                                                            }
                                                        >
                                                            <LuTrash2 size={14} />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal table={table} />
        </div>
    );
};

export default SalesPaymentData;
